import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../services/firebase';
import { useAuth } from '../features/auth/AuthProvider';
import { useToast } from '../features/toast';
import Seo from '../shared/Seo';

const AuthActionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { showToast } = useToast();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleAction = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');
      const continueUrl = searchParams.get('continueUrl');

      // If no action code, redirect to home
      if (!oobCode) {
        setStatus('error');
        setMessage('Invalid verification link.');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      try {
        // Check what type of action this is
        const actionCodeInfo = await checkActionCode(auth, oobCode);

        if (mode === 'verifyEmail' || actionCodeInfo.operation === 'VERIFY_EMAIL') {
          // Handle email verification
          await applyActionCode(auth, oobCode);
          
          // Refresh user to get updated verification status
          if (auth.currentUser) {
            await auth.currentUser.reload();
            await refreshUser?.();
          }

          setStatus('success');
          setMessage('Email verified successfully! 🎉');
          showToast({
            message: 'Email verified! Welcome 🎉',
            tone: 'success',
          });

          // Redirect after 2 seconds
          setTimeout(() => {
            navigate(continueUrl || '/', { replace: true });
          }, 2000);
        } else if (mode === 'resetPassword' || actionCodeInfo.operation === 'PASSWORD_RESET') {
          // Handle password reset - redirect to password reset page with code
          navigate(`/auth/reset-password?oobCode=${oobCode}${continueUrl ? `&continueUrl=${encodeURIComponent(continueUrl)}` : ''}`, { replace: true });
        } else {
          // Unknown action type
          setStatus('error');
          setMessage('Unknown action type.');
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (error) {
        console.error('Failed to handle auth action:', error);
        setStatus('error');
        
        let errorMessage = 'Failed to process verification link.';
        if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'This verification link has expired or is invalid.';
        } else if (error.code === 'auth/expired-action-code') {
          errorMessage = 'This verification link has expired. Please request a new one.';
        }

        setMessage(errorMessage);
        showToast({
          message: errorMessage,
          tone: 'error',
        });

        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleAction();
  }, [searchParams, navigate, refreshUser, showToast]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Seo
        title="Auth Action | PonPon!"
        canonical="https://ponpon.app/auth/action"
        noindex={true}
      />
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        {status === 'processing' && (
          <div className="text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="text-gray-600">Processing verification...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4 text-4xl">✅</div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">Success!</h2>
            <p className="text-gray-600">{message}</p>
            <p className="mt-4 text-sm text-gray-500">Redirecting...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mb-4 text-4xl">❌</div>
            <h2 className="mb-2 text-xl font-semibold text-gray-900">Error</h2>
            <p className="text-gray-600">{message}</p>
            <p className="mt-4 text-sm text-gray-500">Redirecting to home...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthActionPage;

